/**
 * Share links + view analytics.
 *
 * Backend: parkify-sales app/webhooks/shares.py. A share turns a stored
 * proposal into a client-facing URL on proposals.parkify.com; a recipient is
 * a tracked variant of that link so analytics can say who opened it.
 */

export interface ShareRecord {
  id: string
  proposal_id: string
  slug: string
  created_at: string
  revoked_at: string | null
  view_count: number
  unique_view_count: number
  last_viewed_at: string | null
}

export interface RecipientRow {
  token: string
  name?: string | null
  email?: string | null
  created_at: string
  first_viewed_at: string | null
  last_viewed_at: string | null
  view_count: number
  seconds_viewed: number
  downloaded_at: string | null
  opened?: boolean
  /** Fully-qualified tracked link — this is what the rep sends. */
  url?: string
}

export interface TimelineRow {
  type: 'view' | 'download' | 'complete'
  at: string
  recipient_token: string | null
  recipient_name?: string | null
  seconds: number | null
  country?: string | null
}

export interface ShareAnalytics {
  share: {
    id: string
    proposal_id: string
    slug: string
    url: string
    created_at: string
    revoked_at: string | null
  }
  totals: {
    views: number
    unique_viewers: number
    anonymous_views: number
    downloads: number
    completions: number
    seconds_viewed: number
    max_page_reached: number
    last_viewed_at: string | null
  }
  recipients: RecipientRow[]
  timeline: TimelineRow[]
}

async function json<T>(res: Response): Promise<T> {
  const text = await res.text()
  if (!res.ok) {
    try {
      const parsed = JSON.parse(text) as { detail?: string }
      throw new Error(parsed.detail ?? `HTTP ${res.status}`)
    } catch (e) {
      if (e instanceof Error && e.message !== 'Unexpected end of JSON input') throw e
      throw new Error(text || `HTTP ${res.status}`)
    }
  }
  return (text ? JSON.parse(text) : {}) as T
}

/** Current share for a proposal, with analytics. `share: null` = never shared. */
export async function getShare(
  apiBase: string,
  headers: HeadersInit,
  pid: string,
): Promise<{ share: ShareRecord | null; url: string | null; analytics: ShareAnalytics | null }> {
  return json(await fetch(`${apiBase}/proposal/${pid}/share`, { headers }))
}

/** Mint the link. Idempotent — calling twice never invalidates a URL the rep
 *  has already pasted into an email. */
export async function createShare(
  apiBase: string,
  headers: HeadersInit,
  pid: string,
  baseSlug?: string,
): Promise<{ share: ShareRecord; url: string }> {
  return json(
    await fetch(`${apiBase}/proposal/${pid}/share`, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ base_slug: baseSlug || null }),
    }),
  )
}

export async function revokeShare(
  apiBase: string,
  headers: HeadersInit,
  pid: string,
): Promise<void> {
  await json(await fetch(`${apiBase}/proposal/${pid}/share`, { method: 'DELETE', headers }))
}

export async function addRecipient(
  apiBase: string,
  headers: HeadersInit,
  pid: string,
  body: { name?: string; email?: string },
): Promise<{ recipient: RecipientRow; url: string }> {
  return json(
    await fetch(`${apiBase}/proposal/${pid}/share/recipients`, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  )
}

export async function removeRecipient(
  apiBase: string,
  headers: HeadersInit,
  pid: string,
  token: string,
): Promise<void> {
  await json(
    await fetch(`${apiBase}/proposal/${pid}/share/recipients/${token}`, {
      method: 'DELETE',
      headers,
    }),
  )
}

/** Upload a PDF built outside the portal and get back a shareable record. */
export async function uploadProposal(
  apiBase: string,
  headers: HeadersInit,
  body: { file: File; company: string; prospect?: string; city?: string },
): Promise<{ id: string }> {
  const form = new FormData()
  form.append('file', body.file)
  form.append('company', body.company)
  form.append('prospect', body.prospect ?? '')
  form.append('city', body.city ?? '')
  // No Content-Type — the browser must set the multipart boundary itself.
  return json(
    await fetch(`${apiBase}/proposal/upload`, { method: 'POST', headers, body: form }),
  )
}

export function formatDuration(seconds: number): string {
  if (!seconds) return '—'
  if (seconds < 60) return `${seconds}s`
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  if (m < 60) return s ? `${m}m ${s}s` : `${m}m`
  return `${Math.floor(m / 60)}h ${m % 60}m`
}
